import { ErrorState as DsErrorState } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { apiErrorMessage } from '@/lib/api-error'

/**
 * The render states every workflow-administration list and editor shares.
 * Kept in one module so a list can never quietly drop one of them, and so a
 * failure is never presented as an empty result.
 */

/**
 * A read that failed, shown in place of the list or page it would have filled. The server's
 * own message is shown verbatim — a 403 from the backend lands here, never as "nothing to show".
 */
export function ErrorState({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title: string }) {
	return (
		<DsErrorState
			title={title}
			description={apiErrorMessage(error)}
			action={
				onRetry && (
					<Button variant="outline" size="sm" onPress={onRetry}>
						Retry
					</Button>
				)
			}
		/>
	)
}

/** A rejected write, shown inside the form or list that made it. */
export function ErrorBanner({ error, title }: { error: unknown; title: string }) {
	return (
		<div role="alert" className="rounded-ds-base border border-ds-danger/30 bg-ds-danger/10 p-4 text-sm">
			<p className="font-medium text-ds-danger">{title}</p>
			<p className="text-ds-danger">{apiErrorMessage(error)}</p>
		</div>
	)
}

/** A field-level validation message, rendered against the input it belongs to. */
export function FieldError({ message }: { message?: string | null }) {
	if (!message) return null
	return (
		<span role="alert" className="text-xs text-ds-danger">
			{message}
		</span>
	)
}

// Nothing to show. `variant` separates a tenant that has never configured anything from a
// filter that simply matched nothing. The design system owns it; re-exported so this module
// stays the single import for WorkflowAdmin's state components.
export { EmptyState } from '@atlas/ui-components'
